import type {Dispatch, ThunkAction} from 'src/reducers';
import type {FilterColumns} from 'src/types/analytics-genesis';
import {ANALYTICS_API_V4} from 'src/utils/analytics-api-migration';
import {
  surveyV2AllRecipientsTableColumnsQuery,
  surveyV2AllResponsesTableColumnsQuery,
  surveyV2TableColumnsQuery,
} from 'src/utils/analytics-genesis';
import {fetching, key} from 'src/utils/redux';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import * as reduxApi from 'src/utils/redux-api-v2';
import {thunkify as flow} from 'src/utils/thunks';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

export const SET_CURRENT_DASHBOARD_ID =
  'analytics-surveys-v2-dashboard/set-current-dashboard';
export const RECEIVE_SURVEY_V2_TABLE_COLUMNS =
  'analytics-surveys-v2-dashboard/receive-survey-v2-table-columns';
export const RECEIVE_SURVEY_V2_ALL_RESPONSES_TABLE_COLUMNS =
  'analytics-surveys-v2-dashboard/receive-survey-v2-all-responses-table-columns';
export const RECEIVE_SURVEY_V2_ALL_RECIPIENTS_TABLE_COLUMNS =
  'analytics-surveys-v2-dashboard/receive-survey-v2-all-recipients-table-columns';
export const RECEIVE_SURVEY_V2_CHART_COLUMNS =
  'analytics-surveys-v2-dashboard/receive-survey-v2-chart-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

export type ReceiveSurveyV2TableColumnsAction = {
  type: typeof RECEIVE_SURVEY_V2_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveSurveyV2AllResponsesTableColumnsAction = {
  type: typeof RECEIVE_SURVEY_V2_ALL_RESPONSES_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveSurveyV2AllRecipientsTableColumnsAction = {
  type: typeof RECEIVE_SURVEY_V2_ALL_RECIPIENTS_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveSurveyV2ChartColumnsAction = {
  type: typeof RECEIVE_SURVEY_V2_CHART_COLUMNS;
  payload: Array<string>;
};

export type AnalyticsSurveysV2DashboardActions =
  | SetCurrentDashboardIdAction
  | ReceiveSurveyV2TableColumnsAction
  | ReceiveSurveyV2AllResponsesTableColumnsAction
  | ReceiveSurveyV2AllRecipientsTableColumnsAction
  | ReceiveSurveyV2ChartColumnsAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const receiveSurveyV2TableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveSurveyV2TableColumnsAction => ({
  type: RECEIVE_SURVEY_V2_TABLE_COLUMNS,
  payload,
});

export const receiveSurveyV2AllResponsesTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveSurveyV2AllResponsesTableColumnsAction => ({
  type: RECEIVE_SURVEY_V2_ALL_RESPONSES_TABLE_COLUMNS,
  payload,
});

export const receiveSurveyV2AllRecipientsTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveSurveyV2AllRecipientsTableColumnsAction => ({
  type: RECEIVE_SURVEY_V2_ALL_RECIPIENTS_TABLE_COLUMNS,
  payload,
});

export const receiveSurveyV2ChartColumns = (
  payload: Array<string>,
): ReceiveSurveyV2ChartColumnsAction => ({
  type: RECEIVE_SURVEY_V2_CHART_COLUMNS,
  payload,
});

export const getSurveyV2TableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getSurveyV2TableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {columns: FilterColumns} = await dispatch(
      reduxApi.get(
        '/all-surveys-v2/column-selection',
        surveyV2TableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveSurveyV2TableColumns(payload));
  } catch {
    // Error fetching survey v2 table columns
  }
});

export const saveSurveyV2SelectedFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/all-surveys-v2/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getSurveyV2AllResponsesTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getSurveyV2AllResponsesTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          '/all-surveys-v2/column-selection',
          surveyV2AllResponsesTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveSurveyV2AllResponsesTableColumns(payload));
    } catch {
      // Error fetching survey v2 all responses table columns
    }
  });

export const getSurveyV2AllRecipientsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getSurveyV2AllRecipientsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          '/all-surveys-v2/column-selection',
          surveyV2AllRecipientsTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveSurveyV2AllRecipientsTableColumns(payload));
    } catch {
      // Error fetching survey v2 all recipients table columns
    }
  });

export const saveSurveyV2AllRecipientsTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/all-surveys-v2/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const saveSurveyV2AllResponsesTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/all-surveys-v2/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getSurveyV2ChartFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getSurveyV2ChartFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {
      views: Array<string> | null;
    } = await dispatch(
      reduxApi.get(
        '/surveys-v2/save-plot-view',
        surveyV2TableColumnsQuery,
        analyticsOptions,
      ),
    );

    const uniqueViews = Array.from(new Set(payload.views ?? []));

    dispatch(receiveSurveyV2ChartColumns(uniqueViews.slice(0, 10)));
  } catch {
    // Error fetching survey v2 chart filter
  }
});

export const saveSurveyV2SelectedChartFilter: (columnData: {
  views: Array<string>;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    const {views} = columnData;
    return dispatch(
      reduxApi.post(
        '/surveys-v2/save-plot-view',
        {views},
        {},
        analyticsApiOptions,
      ),
    );
  },
);
